import z from "zod";
import { COLLABORATOR_ROLES } from "~/types/collaborator-role";

export { COLLABORATOR_ROLES };

export const industrySchema = z.object({
  category: z.enum(
    ["PUBLICATION", "THESIS", "OTHER", "ARTIST", "WRITER"],
    {
      message: "Please select a category",
    },
  ),
});

export type IndustryFormData = z.infer<typeof industrySchema>;

export const collaboratorSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().min(1, "Email is required").email("Invalid email address"),
  roles: z
    .array(z.enum(COLLABORATOR_ROLES))
    .min(1, "At least one role is required"),
});

export const collaboratorsSchema = z.object({
  collaborators: z
    .array(collaboratorSchema)
    .min(1, "At least one collaborator is required"),
});

export type CollaboratorFormData = z.infer<typeof collaboratorSchema>;
export type CollaboratorsFormData = z.infer<typeof collaboratorsSchema>;

export const isbnSchema = z.object({
  ISBN: z.string().optional(),
  title: z.string().optional(),
});

export type ISBNFormData = z.infer<typeof isbnSchema>;

export const paginationSearchSchema = z.object({});

export const dashboardSearchSchema = z.object({
  scope: z.enum(["mine", "organization"]).optional(),
  search: z.string().optional(),
  status: z.string().optional(),
  sort: z
    .enum(["title", "category", "status", "updatedAt", "owner"])
    .optional(),
  order: z.enum(["asc", "desc"]).optional(),
});

export const DECLARATION_TYPE_VALUES = [
  "author-self-publisher",
  "publisher-representative",
] as const;

export type DeclarationType = (typeof DECLARATION_TYPE_VALUES)[number];

export type PaginationSearchParams = z.infer<typeof paginationSearchSchema>;

export const CONTENT_TYPE_VALUES = ["BOOK"] as const;

export const THESIS_TYPE_VALUES = ["ESSAY", "HONOURS", "MASTERS", "PHD"] as const;
export type ThesisType = (typeof THESIS_TYPE_VALUES)[number];

export type ContentType = (typeof CONTENT_TYPE_VALUES)[number];

export const WRITER_TYPE_VALUES = [
  "HOBBYIST",
  "JOURNALIST",
  "COLUMNIST",
  "TRAVEL_WRITER",
  "BLOGGER",
  "COPYWRITER",
  "WEBSITE",
  "OTHER",
] as const;
export type WriterType = (typeof WRITER_TYPE_VALUES)[number];

export const ARTIST_FIELD_VALUES = [
  "PAINTING",
  "DRAWING",
  "ILLUSTRATION",
  "SCULPTURE",
  "JEWELLERY",
  "CERAMICS",
  "POTTERY",
  "PHOTOGRAPHY",
  "DIGITAL_ART",
  "TEXTILE",
  "OTHER",
] as const;

const normaliseUrlWithProtocol = (value: unknown): unknown => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^[a-z][a-z\d+\-.]*:\/\//i.test(trimmed)
    ? trimmed
    : `https://${trimmed}`;
};

const optionalArtistUrlSchema = z
  .preprocess(normaliseUrlWithProtocol, z.url().or(z.literal("")))
  .optional();

export const applicationFormSchema = z
  .object({
    category: z.enum(
      ["PUBLICATION", "THESIS", "OTHER", "ARTIST", "WRITER"],
      {
        message: "Please select a category",
      },
    ),
    contentType: z.enum(CONTENT_TYPE_VALUES).optional(),
    selectedPublicationId: z.number().optional(),
    ISBN: z.string().optional(),
    title: z.string().optional(),
    authorName: z.string().optional(),
    authorEmail: z.string().optional(),
    publicationDate: z.string().optional(),
    publisher: z.string().optional(),
    declaration: z.boolean(),
    reviewConfirmed: z.boolean().optional(),
    declarationType: z.enum(DECLARATION_TYPE_VALUES).optional(),
    aiUsedForResearch: z.boolean().nullable().optional(),
    aiResearchDescription: z.string().optional(),
    aiUsedInProduction: z.boolean().nullable().optional(),
    aiProductionDescription: z.string().optional(),
    step: z.number(),
    // Thesis-specific fields
    studentName: z.string().optional(),
    courseType: z.string().optional(),
    thesisType: z.enum(THESIS_TYPE_VALUES).optional(),
    // Other-category fields
    workDescription: z.string().optional(),
    certificationReason: z.string().optional(),
    // Writer-category fields
    writerType: z.enum(WRITER_TYPE_VALUES).optional(),
    writerTypeOther: z.string().optional(),
    writerWorkUrls: z.array(z.string()).optional(),
    // Artist-category fields
    artistWorkName: z.string().optional(),
    artistYearOfCompletion: z.number().optional(),
    artistSizeWidth: z.number().optional(),
    artistSizeHeight: z.number().optional(),
    artistSizeUnit: z.enum(["cm", "in"]).optional(),
    artistCountry: z.string().optional(),
    artistCity: z.string().optional(),
    artistField: z.enum(ARTIST_FIELD_VALUES).optional(),
    artistFieldOther: z.string().optional(),
    artistPrimaryMedium: z.string().optional(),
    artistPrimaryMediumOther: z.string().optional(),
    artistSeriesReferenceNumber: z.string().optional(),
    artistSeriesName: z.string().optional(),
    artistSeriesStartYear: z.number().optional(),
    artistArtistName: z.string().optional(),
    artistArtistEmail: z.string().optional(),
    artistArtistAddress: z.string().optional(),
    artistArtistPhone: z.string().optional(),
    artistArtistBackground: z.string().optional(),
    artistWebsiteUrl: optionalArtistUrlSchema,
    artistInstagramUrl: optionalArtistUrlSchema,
    artistFacebookUrl: optionalArtistUrlSchema,
    artistTiktokUrl: optionalArtistUrlSchema,
    artistYoutubeUrl: optionalArtistUrlSchema,
    artistLinkedinUrl: optionalArtistUrlSchema,
    artistXUrl: optionalArtistUrlSchema,
    artistProcessDescription: z.string().optional(),
    artistInspiration: z.string().optional(),
    artistProcessNarrative: z.string().optional(),
    artistAboutWork: z.string().optional(),
    artistDeclarationSigned: z.boolean().optional(),
  });

export type ApplicationFormData = z.infer<typeof applicationFormSchema>;
